import { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { gql, useLazyQuery } from "@apollo/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import EmailValidator from "email-validator";

import useForm from "../../../hooks/useForm";
import { FLAG_ZITADEL_AZURE_LOGIN, FLAG_ZITADEL_GOOGLE_LOGIN } from "../../../includes/featureFlags";
import { hasFeatureFlag } from "../../../includes/site";
import { useAppState } from "../../../contexts/AppContext";
import { AUTH_URL, getUrl, zitadelRoutes } from "../../../includes/routes";
import { LoginInfoManager } from "../../../includes/loginInfoManager";
import { FAILED_AUTHENTICATION_PARAM } from "../../../includes/constants";
import Button from "../../Button";
import Form from "../../form/Form";
import LabelledField from "../../form/LabelledField";
import Input from "../../form/Input";
import FormActions from "../../form/FormActions";
import Loading from "../../utils/Loading";

type ZitadelGetIdpUrlWithKeyVariables = {
    schoolUniqueKey: string;
    idpName: string;
    backUrl: string;
}

type ZitadelGetIdpUrlWithKeyResponse = {
    zitadelGetIdpUrlWithKey: string;
}

type ZitadelGetIdpUrlWithEmailVariables = {
    userEmail: string;
    idpName: string;
    backUrl: string;
}

type ZitadelGetIdpUrlWithEmailResponse = {
    zitadelGetIdpUrlWithEmail: string;
}

const IdpInitialisation = () => {
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const backUrl = searchParams.get(zitadelRoutes.IDP_INITIALISATION.params.BACK_URL) ?? "";
    const idpName = searchParams.get(zitadelRoutes.IDP_INITIALISATION.params.IDP_NAME) ?? "";

    const [isIdpLoading, setIsIdpLoading] = useState(false);
    const [isFallbackRedirectLoading, setIsFallbackRedirectLoading] = useState(false);

    const [appState] = useAppState();
    const isFeatureFlagsLoading = appState.isFeatureFlagsLoading;
    const hasZitadelSsoLoginFlag =
        hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_AZURE_LOGIN) ||
        hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_GOOGLE_LOGIN);

    const { formState, onFieldChange, onFieldInvalid, formErrors, setFormErrors } = useForm({
        fields: { idpIdentifier: LoginInfoManager.get().schoolUniqueKey ?? "" },
    });

    const [zitadelGetIdpUrlWithKey] =
        useLazyQuery<ZitadelGetIdpUrlWithKeyResponse, ZitadelGetIdpUrlWithKeyVariables>(ZITADEL_GET_IDP_URL_WITH_KEY);

    const [zitadelGetIdpUrlWithEmail] =
        useLazyQuery<ZitadelGetIdpUrlWithEmailResponse, ZitadelGetIdpUrlWithEmailVariables>(
            ZITADEL_GET_IDP_URL_WITH_EMAIL
        );

    const onSubmit = useCallback(() => {
        setIsIdpLoading(true);

        if (EmailValidator.validate(formState.idpIdentifier)) {
            zitadelGetIdpUrlWithEmail({
                variables: {
                    userEmail: formState.idpIdentifier,
                    idpName,
                    backUrl,
                },
                fetchPolicy: 'network-only',
            }).then(({ error, data }) => {
                const url = data?.zitadelGetIdpUrlWithEmail;

                // According to Apollo docs the lazy query function always returns a promise.
                // So unfortunately, we have to manually check for an "error".
                if (error || !url) throw new Error("Login attempt did not return a valid response.");

                // Avoids opening the new url in a new tab
                window.location.replace(url);
            }).catch(() => {
                setIsIdpLoading(false);
                setFormErrors({
                    idpIdentifier: "That email address does not match any user. Please enter your school code instead.",
                });
                // Screen reader announces a field error message.
                const firstTargetKey = Object.keys(formErrors)?.[0] ?? null;
                if (firstTargetKey) {
                    const firstElement = document.getElementsByName(Object.keys(formErrors)[0]);
                    if (firstElement.length > 0) {
                        firstElement[0].focus();
                    }
                }
            });
        } else {
            zitadelGetIdpUrlWithKey({
                variables: {
                    schoolUniqueKey: formState.idpIdentifier,
                    idpName,
                    backUrl,
                },
                fetchPolicy: 'network-only',
            }).then(({ error, data }) => {
                const url = data?.zitadelGetIdpUrlWithKey;

                // According to Apollo docs the lazy query function always returns a promise.
                // So unfortunately, we have to manually check for an "error".
                if (error || !url) throw new Error("Login attempt did not return a valid response.");

                // Avoids opening the new url in a new tab
                window.location.replace(url);
            }).catch(() => {
                navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
            });
        }
    }, [
        formState.idpIdentifier,
        zitadelGetIdpUrlWithEmail,
        idpName,
        backUrl,
        setFormErrors,
        formErrors,
        zitadelGetIdpUrlWithKey,
        navigate,
    ]);

    useEffect(function fallbackRedirection() {
        /** SKip fallback redirection if actions are loading since the actions do the redirection themselves */
        if (isIdpLoading) return;

        if (!isFeatureFlagsLoading && !hasZitadelSsoLoginFlag) {
            /** Redirect to start of login flow if missing required feature flags */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        } 
    }, [isFeatureFlagsLoading, hasZitadelSsoLoginFlag, navigate, isIdpLoading]);

    const isLoading = isFeatureFlagsLoading || isFallbackRedirectLoading;
    const isActionsDisabled = isIdpLoading || isLoading;

    if (isLoading) return <Loading onTop overlay overlayColour={"white"} />;

    return (
        <div css={containerStyle}>
            <h1 css={headerStyle}>Sign in with {idpName}</h1>
            <p>
                To log in with {idpName}, you will need to provide your email address or school code.
            </p>
            <div css={formContainerStyle}>
                <Form onSubmit={onSubmit}>
                    <LabelledField
                        name="idpIdentifier"
                        error={formErrors.idpIdentifier}
                        label={"Email Address / School Code"}
                        version={'normal'}
                    >
                        <Input
                        hasError={!!formErrors.idpIdentifier}
                            label={"Enter email address or school code"}
                            name="idpIdentifier"
                            onChange={onFieldChange}
                            onInvalid={onFieldInvalid}
                            value={formState.idpIdentifier}
                            hasBorder
                            required
                        />
                    </LabelledField>
                    <FormActions customCss={formActionsStyle}>
                        <Button
                            css={formButtonStyle}
                            type="submit"
                            forceLoading={isIdpLoading}
                            disabled={isActionsDisabled}
                            useOutline
                        >
                            Log in
                        </Button>
                    </FormActions>
                </Form>
            </div>
        </div>
    );
}

// See SignIn.tsx::authContentStyle
const containerStyle = (theme: EmotionTheme) => css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

// See SignIn.tsx::leftHeaderStyle
const headerStyle = (theme: EmotionTheme) => css`
    margin: 0;
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

const formContainerStyle = css`
    margin-top: 20px;
`;

// See SignIn.tsx::customFormActionsStyles
const formActionsStyle = css`
    width: 100%;
    margin-top: 40px;
    justify-content: center;
`;

// See SignIn.tsx::formSubmitButtonStyle
const formButtonStyle = (theme: EmotionTheme) => css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;

const ZITADEL_GET_IDP_URL_WITH_KEY = gql`
    query zitadelGetIdpUrlWithKey($schoolUniqueKey: String!, $idpName: String!, $backUrl: String) {
        zitadelGetIdpUrlWithKey(schoolUniqueKey: $schoolUniqueKey, idpName: $idpName, backUrl: $backUrl)
    }
`;

const ZITADEL_GET_IDP_URL_WITH_EMAIL = gql`
    query zitadelGetIdpUrlWithEmail($userEmail: String!, $idpName: String!, $backUrl: String) {
        zitadelGetIdpUrlWithEmail(userEmail: $userEmail, idpName: $idpName, backUrl: $backUrl)
    }
`;

export default IdpInitialisation;
